import { useTenantMemberships } from "@/contexts/TenantContext";
import { Colors } from "@/enums/Colors";
import { Spinner } from "@/external/ensolvers-core-frontend-mui-v5/components/Common/Spinner";
import NotificationService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/NotificationService";
import Cross from "@/images/cross-logo.svg";
import CaretSVG from "@/images/data-room/caret.svg";
import Folders from "@/images/folders-logo.svg";
import GreenCheck from "@/images/green-check.png";
import { DataRoomCustomFolder } from "@/models/data-room/DataRoomCustomFolder";
import DataRoomCustomFolderService, {
  DATA_ROOM_CUSTOM_FOLDER_EVENTS,
  DataRoomCustomCreatedFolderEvent,
  DataRoomCustomDeletedFilesEvent,
  DataRoomCustomDeletedFolderEvent,
  DataRoomCustomMovedFilesEvent,
  DataRoomCustomUploadedFilesEvent,
} from "@/services/DataRoomCustomFolderService";
import { Button, Collapse, Popper, SxProps, styled, useTheme } from "@mui/material";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import React, { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from "react";
import { GenericConfirmModal } from "../common/GenericConfirmModal";
import { InputField } from "../utils/InputField";
import LazoTooltip from "../utils/LazoTooltip";
import { DeleteIcon } from "../utils/icons/DeleteIConm";
import { FileIcon } from "../utils/icons/FileIcon";
import { FolderNotch } from "../utils/icons/FolderNotch";
import { DEFAULT_COLORS_ARRAY, DataRoomEntriesList } from "./DataRoomRecursiveList";
import { useDataRoomProvider } from "./contexts/DataRoomContext";

function removeFolder(folder: DataRoomCustomFolder, entries: DataRoomCustomFolder[]): DataRoomCustomFolder[] {
  if (!folder) return entries;

  let clone = structuredClone(entries);

  let stack: DataRoomCustomFolder[] = [];
  for (let i = 0; i < clone.length; i++) {
    if (clone[i].externalId === folder.externalId) {
      clone.splice(i);
      return clone;
    }
    stack.push(clone[i]);
  }

  while (stack.length > 0) {
    const node = stack.pop();

    if (node.nodes) {
      const idx = node.nodes.findIndex((f) => f.externalId === folder.externalId);
      if (idx !== -1) {
        node.nodes.splice(idx);
        break;
      }
    }
  }

  return clone;
}

function addFolder(folder: DataRoomCustomFolder, entries: DataRoomCustomFolder[], parentExternalId?: string): DataRoomCustomFolder[] {
  if (!folder) return entries;

  let clone = structuredClone(entries);

  if (!parentExternalId) {
    clone.push(folder);
    return clone;
  }

  let stack: DataRoomCustomFolder[] = [];
  for (let i = 0; i < clone.length; i++) {
    stack.push(clone[i]);
  }

  while (stack.length > 0) {
    const n = stack.pop();

    if (n.externalId === parentExternalId) {
      if (n.nodes) {
        n.nodes.push(folder);
      } else {
        n.nodes = [folder];
      }
      break;
    }

    if (n.nodes) {
      for (let i = 0; i < n.nodes.length; i++) {
        stack.push(n.nodes[i]);
      }
    }
  }

  return clone;
}

function updateCount(count: number, leafExternalId: string, entries: DataRoomCustomFolder[]) {
  if (!leafExternalId) return entries;

  const cloned = structuredClone(entries);

  const stack: { node: DataRoomCustomFolder; parent?: DataRoomCustomFolder }[] = [];

  // Push root nodes onto the stack
  for (let i = 0; i < cloned.length; i++) {
    stack.push({ node: cloned[i] });
  }

  let targetNode: DataRoomCustomFolder | null = null;
  let path: DataRoomCustomFolder[] = [];

  while (stack.length > 0) {
    const { node, parent } = stack.pop()!;

    while (path.length > 0 && parent?.externalId !== path[path.length - 1].externalId) {
      path.pop();
    }

    if (node.externalId === leafExternalId) {
      targetNode = node;
      break;
    }

    if (node.nodes && node.nodes.length !== 0) {
      for (let i = 0; i < node.nodes.length; i++) {
        stack.push({ node: node.nodes[i], parent: node });
      }
      path.push(node);
    }
  }

  if (targetNode) {
    const s = targetNode.nodeCount + count;
    targetNode.nodeCount = s >= 0 ? s : 0;

    for (const parent of path) {
      const s2 = parent.nodeCount + count;
      parent.nodeCount = s2 >= 0 ? s2 : 0;
    }
  }

  return cloned;
}

function DeleteEntry({ entry, notifyIsOpen }: { entry: DataRoomCustomFolder; notifyIsOpen: (val: boolean) => void }) {
  const [deleting, setDeleting] = useState<boolean>(false);
  const [anchor, setAnchor] = useState<HTMLDivElement | null>(null);

  const ref = useRef<HTMLDivElement>(null);

  function onDelete() {
    setAnchor(ref.current);
    notifyIsOpen(true);
  }

  function onDeleteConfirm() {
    setDeleting(true);
    DataRoomCustomFolderService.delete(entry)
      .then((r) => {
        r.onSuccess(() => notifyIsOpen(false));
        r.notifyErrorIfPresent();
      })
      .finally(() => setDeleting(false));
  }

  return (
    <>
      <Box
        ref={ref}
        onClick={onDelete}
        className="entry-info-container"
        sx={{
          ...OptionsMenuButtonStyle,
          ":hover": {
            backgroundColor: Colors.HEART_RED,
            color: "#fff",
            "& .MuiSvgIcon-root": { fill: "#fff" },
          },
        }}
      >
        <DeleteIcon />
        <Typography fontSize="0.75rem" fontWeight="400" lineHeight="19.6px">
          Delete
        </Typography>
      </Box>
      <GenericConfirmModal
        anchor={anchor}
        setAnchorElement={setAnchor}
        onConfirm={onDeleteConfirm}
        loading={deleting}
        text={"Are you sure you want to delete this folder?"}
        buttonSx={{ backgroundColor: Colors.HEART_RED, ":hover": { backgroundColor: Colors.RED } }}
      />
    </>
  );
}

type FolderCreationEntryProps = {
  onFolderCreationCompleted?: () => void;
  notifyIsOpen?: (val: boolean) => void;
  parentExternalId?: string;
};

function FolderCreationEntry({ onFolderCreationCompleted, notifyIsOpen, parentExternalId }: FolderCreationEntryProps) {
  const [tmpFolder, setTmpFolder] = useState<DataRoomCustomFolder | null>(null);
  const [creating, setCreating] = useState<boolean>();

  const { currentTenantMembership } = useTenantMemberships();

  const innerContainerRef = useRef<HTMLDivElement | null>(null);
  const popperAnchorRef = useRef<HTMLDivElement | null>(null);

  function onCreateFolderClick() {
    if (tmpFolder) {
      close();
      return;
    }
    open();
  }

  function open() {
    setTmpFolder({
      name: "",
    } as DataRoomCustomFolder);
    notifyIsOpen?.(true);
  }

  function onConfirmFolderCreation() {
    if (!tmpFolder || !tmpFolder.name) {
      NotificationService.warn("Folder name cannot be empty");
      return;
    }

    setCreating(true);
    DataRoomCustomFolderService.create(tmpFolder.name, currentTenantMembership.tenant.externalId, parentExternalId)
      .then((r) => {
        r.onSuccess(() => {
          onFolderCreationCompleted?.();
          close();
        });
        r.notifyErrorIfPresent();
      })
      .finally(() => setCreating(false));
  }

  function onKeyDownNewFolderName(ev: React.KeyboardEvent<HTMLDivElement>) {
    if (ev.key === "Enter") {
      onConfirmFolderCreation();
    } else if (ev.key === "Escape") {
      close();
    }
  }

  function onClickOutside(ev: MouseEvent) {
    if (!innerContainerRef.current || !popperAnchorRef.current) return;
    if (!innerContainerRef.current.contains(ev.target as Node) && !popperAnchorRef.current.contains(ev.target as Node)) {
      close();
      window.removeEventListener("mousedown", onClickOutside);
    }
  }

  function close() {
    setTmpFolder(null);
    notifyIsOpen?.(false);
  }

  function onKeyDown(ev: KeyboardEvent) {
    if (ev.key === "Escape") {
      close();
    }
  }

  useEffect(() => {
    window.removeEventListener("mousedown", onClickOutside);
    window.removeEventListener("keydown", onKeyDown);

    window.addEventListener("mousedown", onClickOutside);
    window.addEventListener("keydown", onKeyDown);

    return () => {
      window.removeEventListener("mousedown", onClickOutside);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [innerContainerRef.current, popperAnchorRef.current]);

  const optionPopStyle = {
    width: "15rem",
    borderRadius: "0.25rem",
    boxShadow: "0 0 12px 0 rgba(0, 0, 0, 0.07)",
  };

  const theme = useTheme();

  return (
    <>
      <Box className="entry-info-container" onClick={onCreateFolderClick} sx={{ ...OptionsMenuButtonStyle }} ref={popperAnchorRef}>
        <img src={Folders.src} alt={"folders-svg"} style={{ width: "20px" }} />
        <Typography fontSize="0.75rem" fontWeight="400" lineHeight="19.6px">
          Create Folder
        </Typography>
      </Box>
      <Popper
        sx={{ zIndex: theme.zIndex.tooltip }}
        anchorEl={popperAnchorRef.current}
        open={Boolean(tmpFolder)}
        placement="right"
        modifiers={[
          {
            name: "offset",
            options: { offset: [0, 5] },
          },
        ]}
      >
        <Box gap="0.5rem" sx={optionPopStyle} ref={innerContainerRef}>
          <InputField
            InputProps={{
              startAdornment: <FolderNotch />,
              endAdornment: (
                <>
                  <Box display="flex" alignItems="center" gap="0.25rem">
                    {creating ? (
                      <Spinner size={"1rem"} sx={{ color: Colors.LAZO_PURPLE }} />
                    ) : (
                      <>
                        <SmallButton disabled={creating} disableRipple onClick={() => setTmpFolder(null)}>
                          <img src={Cross.src} alt={"cross-logo"} style={{ width: "16px" }} />
                        </SmallButton>
                        <SmallButton disabled={creating} disableRipple onClick={onConfirmFolderCreation}>
                          <img src={GreenCheck.src} alt={"green-check"} style={{ width: "12px" }} />
                        </SmallButton>
                      </>
                    )}
                  </Box>
                </>
              ),
            }}
            value={tmpFolder?.name || ""}
            onChange={(ev) => setTmpFolder({ name: ev.currentTarget.value } as DataRoomCustomFolder)}
            onKeyDown={(ev) => onKeyDownNewFolderName(ev)}
            placeholder="Name..."
            hideIcon
            sx={{
              "& .MuiInputBase-root": {
                borderRadius: "0.25rem",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                height: "2.25rem",
              },
            }}
          />
        </Box>
      </Popper>
    </>
  );
}

function OptionsContextMenu({
  anchor,
  setAnchor,
  entry,
  folderCreationsProps,
}: {
  anchor: HTMLElement;
  setAnchor: Dispatch<SetStateAction<HTMLElement>>;
  entry: DataRoomCustomFolder;
  folderCreationsProps: FolderCreationEntryProps;
}) {
  const [subMenuOpen, setSubMenuOpen] = useState<boolean>(false);

  const ref = useRef<HTMLDivElement | null>(null);

  function onClickOutside(ev: MouseEvent) {
    if (!ref.current || subMenuOpen) return;
    if (!ref.current.contains(ev.target as Node)) {
      setAnchor(null);
      window.removeEventListener("mousedown", onClickOutside);
    }
  }

  function onKeyDown(ev: KeyboardEvent) {
    if (ev.key === "Escape") {
      setAnchor(null);
    }
  }

  useEffect(() => {
    window.removeEventListener("mousedown", onClickOutside);
    window.removeEventListener("keydown", onKeyDown);

    window.addEventListener("mousedown", onClickOutside);
    window.addEventListener("keydown", onKeyDown);

    return () => {
      window.removeEventListener("mousedown", onClickOutside);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [anchor, subMenuOpen]);

  const theme = useTheme();

  return (
    <Popper
      sx={{ zIndex: theme.zIndex.tooltip }}
      anchorEl={anchor}
      open={Boolean(anchor)}
      ref={ref}
      modifiers={[
        {
          name: "offset",
          options: {
            offset: [30, 0],
          },
        },
      ]}
    >
      <Box sx={ContextMenuStyle}>
        <Typography fontSize="0.75rem" sx={{ color: Colors.DARK_GRAY, padding: "0 0.5rem" }}>
          Options
        </Typography>
        <DataRoomEntriesList level={0}>
          <FolderCreationEntry notifyIsOpen={(val) => setSubMenuOpen(val)} {...folderCreationsProps} />
          <DeleteEntry notifyIsOpen={(val) => setSubMenuOpen(val)} entry={entry} />
        </DataRoomEntriesList>
      </Box>
    </Popper>
  );
}

type TreeNodeProps = {
  entry: DataRoomCustomFolder;
  level: number;
  selected: DataRoomCustomFolder | null;
  setSelected: Dispatch<SetStateAction<DataRoomCustomFolder>>;
};

const TreeNode = React.memo(function TreeNode({ entry, level, selected, setSelected }: TreeNodeProps) {
  const [loading, setLoading] = useState<boolean>(false);
  const [entries, setEntries] = useState<DataRoomCustomFolder[]>([]);
  const [collapsed, setCollapsed] = useState<boolean>(false);

  const { closeAll, setCloseAll } = useDataRoomProvider();

  // In case the reference to entry.nodes changes update it
  useEffect(() => setEntries(entry.nodes || []), [entry.nodes]);
  useEffect(() => {
    if (closeAll) {
      setCollapsed(false);
      setCloseAll(false);
    }
  }, [closeAll]);

  const { currentTenantMembership } = useTenantMemberships();

  const isSelected = selected?.externalId === entry.externalId;

  function onClick(ev: React.MouseEvent<HTMLDivElement>) {
    if (ev.button !== 0) return;

    if (collapsed && isSelected) {
      setCollapsed(false);
      setSelected(null);
      return;
    } else {
      setCollapsed(true);
      setSelected(entry);
    }

    if (entries.length !== 0 && collapsed) return;

    setLoading(true);
    DataRoomCustomFolderService.get(currentTenantMembership.tenant.externalId, entry.externalId)
      .then((r) => {
        r.onSuccess((c) => setEntries(c));
        r.notifyErrorIfPresent();
      })
      .finally(() => setLoading(false));
  }

  const [contextMenuAnchor, setContextMenuAnchor] = useState<HTMLDivElement | null>(null);
  function onContextMenu(ev: React.MouseEvent<HTMLDivElement>) {
    ev.preventDefault();
    setContextMenuAnchor(ev.currentTarget);
  }

  function onFolderCreationCompleted() {
    setCollapsed(true);
    setContextMenuAnchor(null);
  }

  return (
    <>
      <Box className="entry-info-container" onClick={onClick} onContextMenu={onContextMenu} sx={generateEntrySx(isSelected)}>
        <img
          src={CaretSVG.src}
          alt={"caret-svg"}
          style={{
            paddingLeft: "0.25rem",
            transform: collapsed ? "rotate(90deg)" : "",
          }}
        />
        <FolderNotch style={{ fill: DEFAULT_COLORS_ARRAY[level] || "#000" }} />
        <LazoTooltip title={entry.name}>
          <Box sx={EntryNameContainerSx}>
            <Typography sx={{ fontSize: ".875rem", fontWeight: 400, lineHeight: "19.6px", width: "80%" }}>{entry.name}</Typography>
            {entry.nodeCount > 0 && (
              <Typography sx={{ display: "flex", alignItems: "center", gap: ".2rem", marginLeft: "auto" }}>
                <FileIcon />
                {entry.nodeCount}
              </Typography>
            )}
          </Box>
        </LazoTooltip>
      </Box>
      <OptionsContextMenu
        anchor={contextMenuAnchor}
        setAnchor={setContextMenuAnchor}
        entry={entry}
        folderCreationsProps={{ onFolderCreationCompleted, parentExternalId: entry.externalId }}
      />
      {collapsed && (
        <Collapse in={collapsed}>
          {loading ? (
            <Box className="entry-info-container" justifyContent="center">
              <Spinner size={"1rem"} sx={{ color: Colors.LAZO_PURPLE }} />
            </Box>
          ) : (
            <>
              <DataRoomEntriesList level={level + 1}>
                {entries.length === 0 && (
                  <Box className="entry-info-container">
                    <Typography paddingLeft="0.75rem" fontSize="0.75rem" sx={{ color: Colors.DARK_GRAY }}>
                      No folders
                    </Typography>
                  </Box>
                )}
                {entries.map((e) => (
                  <TreeNode key={e.externalId} entry={e} level={level + 1} selected={selected} setSelected={setSelected} />
                ))}
              </DataRoomEntriesList>
            </>
          )}
        </Collapse>
      )}
    </>
  );
});

type Props = {
  level: number;
  setSelected: Dispatch<SetStateAction<DataRoomCustomFolder>>;
  selected: DataRoomCustomFolder;
};

/// If no drilled entries is provided we fetch the user's tree
export function DataRoomCustomFoldersTree({ level, setSelected, selected }: Props) {
  const [entries, setEntries] = useState<DataRoomCustomFolder[]>([]);

  function onUploadedFiles(ev: CustomEvent) {
    const { count, folderExternalId } = ev.detail as DataRoomCustomUploadedFilesEvent;
    setEntries((prev) => updateCount(count, folderExternalId, prev));
  }

  function onMovedFiles(ev: CustomEvent) {
    const { count, toExternalId, fromExternalId } = ev.detail as DataRoomCustomMovedFilesEvent;
    setEntries((prev) => {
      const s1 = updateCount(-count, fromExternalId, prev);
      return updateCount(count, toExternalId, s1);
    });
  }

  function onCreatedFolder(ev: CustomEvent) {
    const { parentExternalId, folder } = ev.detail as DataRoomCustomCreatedFolderEvent;
    setEntries((prev) => addFolder(folder, prev, parentExternalId));
  }

  function onDeletedFolder(ev: CustomEvent) {
    const { folder } = ev.detail as DataRoomCustomDeletedFolderEvent;
    setEntries((prev) => {
      const s1 = updateCount(-folder.nodeCount, folder.externalId, prev);
      return removeFolder(folder, s1);
    });
  }

  function onDeletedFiles(ev: CustomEvent) {
    const { count, fromExternalId } = ev.detail as DataRoomCustomDeletedFilesEvent;
    setEntries((prev) => updateCount(-count, fromExternalId, prev));
  }

  useMemo(() => {
    document.removeEventListener(DATA_ROOM_CUSTOM_FOLDER_EVENTS.UPLOADED_FILES, onUploadedFiles);
    document.removeEventListener(DATA_ROOM_CUSTOM_FOLDER_EVENTS.MOVED_FILES, onMovedFiles);
    document.removeEventListener(DATA_ROOM_CUSTOM_FOLDER_EVENTS.CREATED_FOLDER, onCreatedFolder);
    document.removeEventListener(DATA_ROOM_CUSTOM_FOLDER_EVENTS.DELETED_FOLDER, onDeletedFolder);
    document.removeEventListener(DATA_ROOM_CUSTOM_FOLDER_EVENTS.DELETED_FILES, onDeletedFiles);

    document.addEventListener(DATA_ROOM_CUSTOM_FOLDER_EVENTS.UPLOADED_FILES, onUploadedFiles);
    document.addEventListener(DATA_ROOM_CUSTOM_FOLDER_EVENTS.MOVED_FILES, onMovedFiles);
    document.addEventListener(DATA_ROOM_CUSTOM_FOLDER_EVENTS.CREATED_FOLDER, onCreatedFolder);
    document.addEventListener(DATA_ROOM_CUSTOM_FOLDER_EVENTS.DELETED_FOLDER, onDeletedFolder);
    document.addEventListener(DATA_ROOM_CUSTOM_FOLDER_EVENTS.DELETED_FILES, onDeletedFiles);

    return () => {
      document.removeEventListener(DATA_ROOM_CUSTOM_FOLDER_EVENTS.UPLOADED_FILES, onUploadedFiles);
      document.removeEventListener(DATA_ROOM_CUSTOM_FOLDER_EVENTS.MOVED_FILES, onMovedFiles);
      document.removeEventListener(DATA_ROOM_CUSTOM_FOLDER_EVENTS.CREATED_FOLDER, onCreatedFolder);
      document.removeEventListener(DATA_ROOM_CUSTOM_FOLDER_EVENTS.DELETED_FOLDER, onDeletedFolder);
      document.removeEventListener(DATA_ROOM_CUSTOM_FOLDER_EVENTS.DELETED_FILES, onDeletedFiles);
    };
  }, []);

  const { currentTenantMembership } = useTenantMemberships();

  useEffect(() => {
    DataRoomCustomFolderService.getTree(currentTenantMembership.tenant.externalId).then((r) => {
      r.onSuccess((c) => setEntries(c));
      r.notifyErrorIfPresent();
    });
  }, [currentTenantMembership.tenant.externalId]);

  return (
    <>
      <DataRoomEntriesList level={level} key={"parent"}>
        <FolderCreationEntry />
        {entries.map((e) => (
          <TreeNode key={e.externalId} entry={e} level={level} selected={selected} setSelected={setSelected} />
        ))}
        {entries.length > 0 && entries.length < 4 && (
          <Typography fontSize="0.75rem" textAlign="center" sx={{ cursor: "default", color: Colors.DARKER_GRAY }}>
            Right-click on a folder to create one
          </Typography>
        )}
      </DataRoomEntriesList>
    </>
  );
}

const OptionsMenuButtonStyle = {
  transition: "all 0.1s ease-in",
  "&:hover": {
    cursor: "pointer",
    borderRadius: "0.5rem",
    backgroundColor: Colors.BUTTON_SECONDARY_HOVER_PURPLE,
  },
  "&.entry-info-container": {
    padding: "0 0.5rem",
  },
};

const SmallButton = styled(Button)(() => ({
  display: "flex",
  height: "1.5rem",
  minHeight: "1.5rem",
  maxHeight: "1.5rem",
  width: "1.25rem",
  minWidth: "1.25rem",
  maxWidth: "1.25rem",
  padding: "0 0.25rem",
  ":hover": {
    backgroundColor: Colors.BUTTON_SECONDARY_HOVER_PURPLE,
  },
}));

const ContextMenuStyle = {
  display: "flex",
  flexDirection: "column",
  padding: "0.25rem 0 0 0",
  backgroundColor: "#fff",
  border: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`,
  borderRadius: "0.25rem",
  boxShadow: "0 0 12px 0 rgba(0, 0, 0, 0.1)",
  minWidth: "12.5rem",
};

const generateEntrySx = (isSelected: boolean): SxProps => ({
  backgroundColor: isSelected ? Colors.BUTTON_SECONDARY_HOVER_PURPLE : "inherit",
  "&:hover": {
    cursor: "pointer",
    borderRadius: "0.5rem",
    backgroundColor: !isSelected ? Colors.BUTTON_SECONDARY_HOVER_PURPLE : Colors.ACCENT_BLUE_DESATURATED_CED5FD,
  },
});

const EntryNameContainerSx: SxProps = {
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  width: "82%",
  "@media (max-width: 1441px)": {
    paddingRight: "0.625rem",
  },
};
